import { useState, useCallback } from 'react';
import type { Store } from '../types';
import {
    fetchStoresFromDb,
    fetchStoreByIdFromDb,
    createStoreInDb,
    updateStoreInDb,
    deleteStoreFromDb,
    clearDefaultStoreExcept,
    getLinkedProductCount,
    clearAllDefaultStoresFromDb,
    type StorePayload,
} from '../services/storeService';
import { getErrorMessage } from '../utils/errors';

export function useStores() {
    const [stores,  setStores]  = useState<Store[]>([]);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState<string | null>(null);

    const fetchStores = useCallback(async (): Promise<Store[]> => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchStoresFromDb();
            setStores(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch stores'));
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchStoreById = useCallback(async (storeId: string): Promise<Store | null> => {
        try {
            return await fetchStoreByIdFromDb(storeId);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch store'));
            return null;
        }
    }, []);

    const createStore = useCallback(async (payload: StorePayload): Promise<Store | null> => {
        setError(null);
        try {
            if (payload.default_store) {
                await clearAllDefaultStoresFromDb();
            }
            const created = await createStoreInDb(payload);
            setStores(prev => [created, ...prev]);
            return created;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create store'));
            return null;
        }
    }, []);

    const updateStore = useCallback(async (id: string, payload: Partial<StorePayload>): Promise<Store | null> => {
        setError(null);
        try {
            if (payload.default_store) {
                await clearDefaultStoreExcept(id);
            }
            const updated = await updateStoreInDb(id, payload);
            setStores(prev => prev.map(s => s.id === id ? updated : s));
            return updated;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update store'));
            return null;
        }
    }, []);

    const deleteStore = useCallback(async (id: string): Promise<{ success: boolean; linkedCount?: number }> => {
        setError(null);
        try {
            const linkedCount = await getLinkedProductCount(id);
            if (linkedCount > 0) return { success: false, linkedCount };
            await deleteStoreFromDb(id);
            setStores(prev => prev.filter(s => s.id !== id));
            return { success: true };
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to delete store'));
            return { success: false };
        }
    }, []);

    return {
        stores,
        loading,
        error,
        fetchStores,
        fetchStoreById,
        createStore,
        updateStore,
        deleteStore,
        clearAllDefaultStoresFromDb,
    };
}